import * as React from "react";
import {
  ComparableProperty,
  PresentationSourceData,
  PropertyPhoto,
  SectionConfig,
  SectionView,
} from "@cma/presentation-generation-logic/models";
import { SectionDataBase } from "@cma/presentation-generation-logic/models/section-data/section-data-base";
import { HeroHeaderEdit } from "./HeroHeader";
import { InsightsEdit } from "./Insights";
import { KeyFeaturesEdit } from "./KeyFeatures";
import { NeighborhoodMap } from "./NeighborhoodMap";
import { SideBySideComparisonEdit } from "./SideBySideComparison";
import { FreeHtmlText } from "./FreeHtmlText";
import { BrandingEdit } from "./Branding";
import { TeamBrandingEdit } from "./TeamBranding";
import { PresentationSectionOnChangeHandler } from "./PresentationSectionProps";
import { Box, Typography, CircularProgress } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";

const PendingOverlayContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: ${(p) => `${p.theme.colors.background}66`};
  backdrop-filter: blur(3px);
  z-index: 5;
  border-radius: inherit;
`;

const SectionPendingOverlay = () => (
  <PendingOverlayContainer>
    <CircularProgress size={28} thickness={4} />
    <Typography variant="body2" color="textSecondary">
      Preparing content…
    </Typography>
  </PendingOverlayContainer>
);

interface Props {
  sectionView: SectionView;
  sectionConfig: SectionConfig;
  displayMode: "mobile" | "desktop" | "print" | "tablet";
  galleries: { [key: string]: PropertyPhoto[] };
  editMode?: boolean;
  sourceData: PresentationSourceData;
  onUpdateSection: PresentationSectionOnChangeHandler;
  onDeleteSection: (id: string) => void;
  onOpenFindComparablePropertiesWidget: () => void;
  handleRemoveComparableProperty: (
    comparableProperty: ComparableProperty,
  ) => void;
  handleUpdateComparableProperty: (
    comparableProperty: ComparableProperty,
  ) => void;
  handleReorderComparableProperties: (
    comparableProperties: ComparableProperty[],
  ) => void;
  isEditing?: boolean;
  onIsEditingChange?: (editing: boolean) => void;
  isPending?: boolean;
  sectionData?: SectionDataBase;
  // onFetchSectionsData?: () => void;
}

export const PresentationSectionRenderer = React.memo((props: Props) => {
  const {
    sectionView,
    sectionConfig,
    displayMode,
    galleries,
    sourceData,
    onUpdateSection,
    onDeleteSection,
    onOpenFindComparablePropertiesWidget,
    handleRemoveComparableProperty,
    handleUpdateComparableProperty,
    handleReorderComparableProperties,
    isEditing,
    onIsEditingChange,
    isPending,
  } = props;

  let content: React.ReactElement | null = null;

  if (sectionView.type === "header" && sectionConfig.type === "header") {
    content = (
      <HeroHeaderEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
      />
    );
  } else if (
    sectionView.type === "header-branding" &&
    sectionConfig.type === "header-branding"
  ) {
    content = (
      <HeroHeaderEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
      />
    );
  } else if (
    sectionView.type === "key-features" &&
    sectionConfig.type === "key-features"
  ) {
    content = (
      <KeyFeaturesEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
      />
    );
  } else if (
    sectionView.type === "side-by-side-comparision" &&
    sectionConfig.type === "side-by-side-comparision"
  ) {
    content = (
      <SideBySideComparisonEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        galleries={galleries}
        sourceData={sourceData}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        onOpenFindComparablePropertiesWidget={
          onOpenFindComparablePropertiesWidget
        }
        handleRemoveComparableProperty={handleRemoveComparableProperty}
        handleUpdateComparableProperty={handleUpdateComparableProperty}
        handleReorderComparableProperties={handleReorderComparableProperties}
      />
    );
  } else if (
    sectionView.type === "free-html-text" &&
    sectionConfig.type === "free-html-text"
  ) {
    content = (
      <FreeHtmlText
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
        isEditing={isEditing}
        onIsEditingChange={onIsEditingChange}
      />
    );
  } else if (
    sectionView.type === "ai-assisted-text" &&
    sectionConfig.type === "ai-assisted-text"
  ) {
    content = (
      <FreeHtmlText
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
        isEditing={isEditing}
        onIsEditingChange={onIsEditingChange}
      />
    );
  } else if (
    sectionView.type === "branding" &&
    sectionConfig.type === "branding"
  ) {
    content = (
      <BrandingEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
        displayMode={displayMode}
      />
    );
  } else if (
    sectionView.type === "team-branding" &&
    sectionConfig.type === "team-branding"
  ) {
    content = (
      <TeamBrandingEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
        displayMode={displayMode}
      />
    );
  } else if (
    sectionView.type === "insights" &&
    sectionConfig.type === "insights"
  ) {
    content = (
      <InsightsEdit
        sectionView={sectionView}
        sectionConfig={sectionConfig}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        sourceData={sourceData}
        isEditing={isEditing}
        onIsEditingChange={onIsEditingChange}
      />
    );
  } else if (
    sectionView.type === "neighborhood-map" &&
    sectionConfig.type === "neighborhood-map"
  ) {
    content = (
      <NeighborhoodMap sectionView={sectionView} galleries={galleries} />
    );
  }

  if (!content) {
    console.warn(
      `PresentationSectionRenderer: Section type "${sectionView.type}" not implemented.`,
    );
    return null;
  }

  return (
    <Box
      position="relative"
      sx={{ minHeight: isPending ? "320px" : undefined }}
    >
      {content}
      {isPending && <SectionPendingOverlay />}
    </Box>
  );
});

PresentationSectionRenderer.displayName = "PresentationSectionRenderer";
